import { useEffect, useMemo, useState } from "react";
import api from "../utils/api";
import { PortalReview, PortalReviewFile } from "../types/PortalReview";

const cache = new Map<string, string>();

function cacheKey(reviewId: number, fileId: string) {
  return `${reviewId}:${fileId}`;
}

export function useReviewFileUrl(
  review: PortalReview | null,
  file: PortalReviewFile | null,
) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const key = useMemo(() => {
    if (!review || !file) return "";
    return cacheKey(review.review_id, file.boxFileId);
  }, [review, file]);

  useEffect(() => {
    if (!review || !file || !key) {
      setUrl(null);
      return;
    }

    const cached = cache.get(key);
    if (cached) {
      setUrl(cached);
      setError(null);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setLoading(true);
    setError(null);

    api
      .get(`/allocation-tool/portal-reviews/${review.review_id}/files/${file.boxFileId}`, {
        responseType: "blob",
        signal: controller.signal,
      })
      .then((res: { data: Blob }) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(res.data);
        cache.set(key, objectUrl);
        setUrl(objectUrl);
      })
      .catch((err: { response?: { data?: { message?: string } } }) => {
        if (cancelled) return;
        setError(err?.response?.data?.message || "Could not load file");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [key, review, file]);

  return { url, loading, error };
}
